import React from 'react';
import AmountPriceContainer, {
  createAmountPropsFromState,
  createBulkNameErrors,
  createPricePropsFromState,
} from '@configuratorware/configurator-frontendgui/App/Shared/Containers/AmountPrice';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';
import { isBulkNamesSelected } from '@configuratorware/configurator-frontendgui/App/Reducers/DesignArea/Selectors';
import isArray from 'lodash/isArray';
import get from 'lodash/get';
import createSelector from '@configuratorware/configurator-frontendgui/Utils/Function/createSelector';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { connect } from 'react-redux';
import isPlainObject from 'lodash/isPlainObject';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import {
  getSelectedAmounts,
  getItem,
  getCalculation,
  getUserSelectedCalculation,
  getClientTexts,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Selectors';
import { isNumber } from 'lodash';
import {
  getSortedPmsAmounts,
  getSurcharge,
  getMaxAmount,
} from '../../../Reducers/Configurator/Selectors';
import { PMS_REGEX } from '../../../../../lib/constants';

import AmountPrice from '../../Components/AmountPrice';

const tools = [
  {
    key: 'save',
    identifier: 'cloud_download',
    title: 'tools.save',
    function: () => Services.interaction.saveConfiguration(),
    enabled: getConf('availableTools.save'),
  },
  {
    key: 'load',
    identifier: 'cloud_upload',
    title: 'tools.load',
    function: () => {
      Services.interaction.showLoadConfigurationDialog();
    },
    enabled: getConf('availableTools.load'),
  },
  {
    key: 'mail',
    identifier: 'mail',
    title: 'tools.sendEmail',
    function: async () => {
      await Services.interaction.mailConfiguration();
      Services.interaction.showSendEmailDialog();
    },
    enabled: getConf('availableTools.share'),
  },
  {
    key: 'picture_as_pdf',
    identifier: 'picture_as_pdf',
    title: 'tools.saveAsPdf',
    function: () => Services.interaction.printConfigurationAndOpenPdf(),
    enabled: getConf('availableTools.print'),
  },
];

const getProductLabel = (p) => `${p.identifier} ${p.title}`;

const createPMSState = createSelector(
  getUserSelectedCalculation,
  (userSelectedCalculation) => ({
    pmsIsSelected: userSelectedCalculation
      ? userSelectedCalculation['global_PMS-Special-Color'] || false
      : false,
  }),
);
const isLargestTier = (idx, sortedPms, amount) => {
  if (idx === sortedPms.length - 1) return amount >= sortedPms[idx].minAmount;
  return (
    amount < sortedPms[idx + 1].minAmount && amount >= sortedPms[idx].minAmount
  );
};
const canSelectPMSAttributes = (position, attributes, amount, sortedPms) => {
  if (
    !(position.calculationTypeIdentifier || '').match(PMS_REGEX)
    || !sortedPms
  ) { return { isDisabled: false, dontRender: false }; }
  let minAm = position.calculationTypeIdentifier.split(PMS_REGEX)[1];
  const globalMinAm = sortedPms[0].minAmount;
  if (minAm) minAm = parseFloat(minAm);
  const minAmountValue = sortedPms[0].minAmount;
  if (minAm < minAmountValue) {
    return {
      dontRender: true,
    };
  }
  if (!isNumber(minAm)) {
    return {
      isDisabled: amount < minAmountValue,
      dontRender: amount < minAmountValue,
      tooltip: t('amountPrice.pmsInfo', { minAmountPMS: minAmountValue }),
    };
  }

  const idx = sortedPms.findIndex((pms) => parseFloat(pms.minAmount) === minAm);
  const largestTier = isLargestTier(idx, sortedPms, amount);
  return {
    isDisabled: !largestTier,
    dontRender: !largestTier && !(idx === 0 && amount < sortedPms[0].minAmount),
    tooltip: t('amountPrice.pmsInfo', { minAmountPMS: minAm }),
  };
};

const isPmsIdentifier = (position) => (position?.calculationTypeIdentifier || '').match(PMS_REGEX);

const createCustomPricePropsFromState = createSelector(
  ({ configurator }) => getCalculation(configurator),
  ({ configurator }) => getUserSelectedCalculation(configurator),
  ({ configurator }) => getItem(configurator),
  ({ configurator }) => getSelectedAmounts(configurator),
  (state) => getSortedPmsAmounts(state),
  (state) => getSurcharge(state),
  (
    calculation,
    userSelectedCalculation,
    item,
    selectedAmounts,
    sortedPms,
    surcharges,
  ) => {
    const displayNetPrices = getConf('displayNetPrices', false);
    const props = {
      priceList: [],
      notifications: [],
    };

    if (props.error && props.error.errors.length) {
      const uniqErrors = uniqBy(props.error.errors, 'itemIdentifier');
      for (const fieldError of uniqErrors) {
        props.notifications.push({
          message: t('amountPrice.minimumAmountError', {
            minimumAmount: fieldError.minimumAmount,
            itemTitle: getProductLabel(item),
            selectedAmount: fieldError.selectedAmount,
          }),
          level: 'error',
          color: 'error',
        });
      }
    }
    const bulkSavings = get(calculation, 'bulkSavings');
    if (isArray(bulkSavings)) {
      props.bulkSavings = bulkSavings.map((b) => ({
        key: b.itemIdentifier,
        itemTitle: getProductLabel(item),
        savingsBulkAmount: b.savingsBulkAmount,
        savingAmount: b.savingAmount,
      }));

      props.bulkSavings.map((bulkSaving) => {
        props.notifications.push({
          message: t('amountPrice.bulkSavings', {
            itemTitle: getProductLabel(item),
            savingsBulkAmount: bulkSaving.savingsBulkAmount,
            savingAmount: bulkSaving.savingAmount,
          }),
          level: 'info',
          color: 'default',
        });
      });
    }

    let amount = 0;
    const positions = get(calculation, 'positions');
    const attributes = get(item, 'attributes');
    if (isPlainObject(selectedAmounts)) {
      amount = Object.values(selectedAmounts).reduce(
        (sum, amount) => sum + amount,
        0,
      );
    }
    if (isArray(positions)) {
      const priceListWithoutSurcharges = [];
      const surchargesList = [];
      positions.forEach((position) => {
        if (position.calculationTypeIdentifier.includes('global_surcharge')) {
          surchargesList.push(position);
        } else {
          priceListWithoutSurcharges.push(position);
        }
      });
      const priceListPms = [];
      priceListWithoutSurcharges.forEach((p) => {
        if (p) {
          const productItem = {
            key: p.calculationTypeIdentifier,
            selectable: p.selectableByUser,
            selected: get(
              userSelectedCalculation,
              p.calculationTypeIdentifier,
              false,
            ),
            label:
              p.title || t(`calculationTypes.${p.calculationTypeIdentifier}`),
            price: displayNetPrices ? p.priceNetFormatted : p.priceFormatted,
            ...canSelectPMSAttributes(p, attributes, amount, sortedPms),
          };
          if (isPmsIdentifier(p)) {
            priceListPms.push(productItem);
          } else if (p.calculationTypeIdentifier === 'item') {
            props.priceList.push({
              ...productItem,
              label: getProductLabel(item),
            });
          } else {
            props.priceList.push(productItem);
          }
        }
      });

      if (surchargesList.length > 0) {
        let activeSurchargeKey = -1;
        const surchargeKeys = Object.keys(surcharges);
        if (surchargeKeys.length > 0) {
          const maxSurchargeKey = surchargeKeys[surchargeKeys.length - 1];
          // const maxSurchargeAmount = surcharges[maxSurchargeKey].to.value + 1;
          const activeSurcharge = surchargeKeys.find((key) => {
            activeSurchargeKey = key;
            return (
              surcharges[key].from.value <= amount
              && surcharges[key].to.value >= amount
            );
          });
          if (activeSurcharge === undefined) activeSurchargeKey = -1;
          let formattedValue = -1;
          surchargesList.forEach((surcharge) => {
            let activateSurcharge = false;
            if (activeSurchargeKey > -1) {
              activateSurcharge = surcharges[activeSurchargeKey].value === surcharge.priceNet;
              if (
                activateSurcharge
                && !get(
                  userSelectedCalculation,
                  `global_surcharge_${surcharges[activeSurchargeKey].value}`,
                  false,
                )
              ) {
                Services.configurator.setSelectedCalculation(
                  `global_surcharge_${surcharges[activeSurchargeKey].value}`,
                  true,
                );
              }
            }
            if (
              !activateSurcharge
              && get(
                userSelectedCalculation,
                `global_surcharge_${surcharge.priceNet}`,
                true,
              )
            ) {
              Services.configurator.setSelectedCalculation(
                `global_surcharge_${surcharge.priceNet}`,
                false,
              );
            }
            if (activateSurcharge) formattedValue = surcharge.priceNetFormatted;
          });

          if (activeSurcharge > -1) {
            props.priceList = [
              ...props.priceList,
              {
                key: 'surcharge',
                selectable: false,
                selected: true,
                label: 'Einmalaufschlag Mindermenge',
                price: formattedValue,
                dontRender: false,
                //    tooltipText: `Bei Bestellungen unter ${maxSurchargeAmount} Stück fallen Einmalaufschläge an.`,
              },
            ];
          }
        }
      }
      props.priceList = [...props.priceList, ...priceListPms];
      // Services.configurator.setSelectedCalculation(`global_surcharge_${surcharges[activeSurchargeKey].value}`, true);
    }
    props.title = getProductLabel(item);
    return props;
  },
);

// TODO Entfernen, ich weiß nicht wie ich das auf die schnelle anders machen kann......
const checkDesignDataIntegrity = (state) => {};
const mapStateToProps = (state) => ({
  showProductVariants: true,
  showReceiveOfferForm: getConf('displayReceiveOfferForm', true),
  displayAcceptDesign: getConf('displayAcceptDesign', true),
  isBulkNamesSelected: isBulkNamesSelected(state.configurator),
  bulkNameErrors: isBulkNamesSelected(state.configurator)
    ? createBulkNameErrors(state.configurator)
    : null,
  ...createAmountPropsFromState(state.configurator),
  ...createPricePropsFromState(state.configurator),
  ...createCustomPricePropsFromState(state),
  clientTexts: getClientTexts(state.configurator),
  tools: tools.filter((tool) => tool.enabled),
  max: getMaxAmount(state),
  onAddToBasket: () => Services.interaction._addToCart(),
  itemAttributes: state.configurator.configuration.item.attributes,
  sortedPMS: state.customConfigurator.sortedPmsAmounts,
  onSelectableChanged: (identifier, value) => {
    const {
      userSelectedCalculation,
    } = state.configurator.configuration.customdata;
    if (identifier.match(PMS_REGEX) && !!userSelectedCalculation) {
      Object.keys(userSelectedCalculation).forEach((key) => {
        if (key !== identifier) {
          if (key.match(PMS_REGEX)) {
            if (userSelectedCalculation[key]) {
              Services.configurator.setSelectedCalculation(key, false);
            }
          }
        }
      });
    }

    Services.configurator.setSelectedCalculation(identifier, value);
  },
  ...createPMSState(state.configurator),
});

export default connect(mapStateToProps)((props) => (
  <AmountPriceContainer
    {...props}
    tools={tools.filter((tool) => tool.enabled)}
    Component={AmountPrice}
  />
));
